<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import {
  buildConfigDiff,
  fieldLabelKey,
  isComplexField,
} from 'dashboard/routes/dashboard/captain/assistants/prompt_versions/configDiff';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['restored']);

const { t } = useI18n();
const store = useStore();
const { relativeTime } = useLocalizedTime();

const uiFlags = useMapGetter('captainPromptVersions/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const promptVersions = useMapGetter('captainPromptVersions/getRecords');

const restoreDialogRef = ref(null);
const versionPendingRestore = ref(null);
const expandedVersionId = ref(null);

const authorName = version =>
  version.author?.name ||
  t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.UNKNOWN_AUTHOR');

const toggleChanges = version => {
  expandedVersionId.value =
    expandedVersionId.value === version.id ? null : version.id;
};

const fieldLabel = field => {
  const key = fieldLabelKey(field);
  return key ? t(key) : field;
};

const changesForVersion = index => {
  const version = promptVersions.value[index];
  const previousVersion = promptVersions.value[index + 1];
  if (!previousVersion) return [];

  return buildConfigDiff(
    previousVersion.config_snapshot,
    version.config_snapshot
  );
};

const askRestoreConfirmation = version => {
  versionPendingRestore.value = version;
  restoreDialogRef.value?.open();
};

const fetchVersions = () =>
  store.dispatch('captainPromptVersions/get', {
    assistantId: props.assistantId,
  });

const restoreVersion = async () => {
  if (!versionPendingRestore.value) return;

  try {
    await store.dispatch('captainPromptVersions/restore', {
      id: versionPendingRestore.value.id,
      assistantId: props.assistantId,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.API.RESTORE.SUCCESS'));
    fetchVersions();
    emit('restored');
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.API.RESTORE.ERROR'));
  } finally {
    versionPendingRestore.value = null;
    restoreDialogRef.value?.close();
  }
};

onMounted(fetchVersions);
</script>

<template>
  <div v-if="isFetching" class="flex items-center justify-center py-10">
    <Spinner />
  </div>
  <div v-else-if="promptVersions.length === 0">
    <span class="text-sm text-n-slate-11">
      {{ t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.EMPTY_MESSAGE') }}
    </span>
  </div>
  <div v-else class="flex flex-col gap-2">
    <CardLayout
      v-for="(version, index) in promptVersions"
      :key="version.id"
      layout="row"
    >
      <div class="flex flex-col gap-1 min-w-0">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            index === 0
              ? t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.CURRENT_VERSION')
              : relativeTime(version.created_at)
          }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{
            t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.AUTHORED_BY', {
              author: authorName(version),
            })
          }}
        </span>
      </div>
      <div class="flex items-center gap-1">
        <Button
          v-if="index !== promptVersions.length - 1"
          v-tooltip.top="t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.VIEW_CHANGES')"
          icon="i-lucide-chevron-down"
          :class="{ 'rotate-180': expandedVersionId === version.id }"
          class="transition-transform duration-200"
          type="button"
          ghost
          sm
          slate
          @click="toggleChanges(version)"
        />
        <Button
          v-if="index !== 0"
          v-tooltip.top="t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.RESTORE')"
          icon="i-lucide-history"
          type="button"
          ghost
          sm
          slate
          @click="askRestoreConfirmation(version)"
        />
      </div>

      <template v-if="expandedVersionId === version.id" #after>
        <div class="flex flex-col gap-2 px-6 pb-5 -mt-2">
          <span
            v-if="changesForVersion(index).length === 0"
            class="text-sm text-n-slate-11"
          >
            {{ t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.NO_CHANGES') }}
          </span>
          <div
            v-for="change in changesForVersion(index)"
            :key="change.field"
            class="text-sm text-n-slate-11"
          >
            <span class="font-medium text-n-slate-12">
              {{ fieldLabel(change.field) }}:
            </span>
            <template v-if="isComplexField(change.field)">
              {{ t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.FIELD_CHANGED') }}
            </template>
            <template v-else>
              {{
                t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.FIELD_CHANGE', {
                  from: change.from ?? '—',
                  to: change.to ?? '—',
                })
              }}
            </template>
          </div>
        </div>
      </template>
    </CardLayout>
  </div>

  <Dialog
    ref="restoreDialogRef"
    type="alert"
    :title="t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.RESTORE_DIALOG.TITLE')"
    :description="
      t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.RESTORE_DIALOG.DESCRIPTION')
    "
    :confirm-button-label="
      t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.RESTORE_DIALOG.CONFIRM')
    "
    @confirm="restoreVersion"
  />
</template>
